import { useCallback, useMemo } from "react";

import { useGetPropertyLocationHierarchyQuery } from "graphql/queries/get-property-location-hierarchy.generated";
import { StructuredLocationLabels } from "utils/location-hierarchy";

interface LocationIds {
	building_id?: string | null;
	floor_id?: string | null;
	room_id?: string | null;
}

/**
 * Resolves building/floor/room ids to their names for a single property.
 *
 * Used by the device-detection screens, whose draft installations only carry the FK ids
 * (the tree is not denormalized into the draft). Real installed_products rows already
 * expose the names through their `building/floor/room { name }` relationships and should use
 * `labelsFromRelationships` instead — no need for this hook.
 */
export const usePropertyLocationLabels = (propertyId?: string | null) => {
	const { data } = useGetPropertyLocationHierarchyQuery({
		variables: { propertyId: propertyId as string },
		skip: !propertyId,
		fetchPolicy: "cache-and-network"
	});

	const nameById = useMemo(() => {
		const buildings = new Map<string, string>();
		const floors = new Map<string, string>();
		const rooms = new Map<string, string>();
		(data?.buildings ?? []).forEach(b => {
			buildings.set(b.id, b.name);
			(b.floors ?? []).forEach(f => {
				floors.set(f.id, f.name);
				(f.rooms ?? []).forEach(r => rooms.set(r.id, r.name));
			});
		});
		return { buildings, floors, rooms };
	}, [data]);

	const getLabels = useCallback(
		(loc?: LocationIds | null): StructuredLocationLabels => ({
			buildingName: loc?.building_id ? (nameById.buildings.get(loc.building_id) ?? null) : null,
			floorName: loc?.floor_id ? (nameById.floors.get(loc.floor_id) ?? null) : null,
			roomName: loc?.room_id ? (nameById.rooms.get(loc.room_id) ?? null) : null
		}),
		[nameById]
	);

	return { getLabels };
};
